import { Center, Flex, Stack } from "@mantine/core";
import { forwardRef, useCallback, useMemo } from "react";
import { type ChatTask, useActiveChatMessages } from "@/sdk/agent/chat";
import { getSessionByTaskId } from "@/sdk/sessions/getters";
import classes from "../chat-components.module.css";
import { ChatAvatar } from "./ChatAvatar";
import { ChatPreviewBuilder } from "./ChatPreviewBuilder";
import { NotificationTimerRing } from "./NotificationTimerRing";
import { QueuedChatIndicator } from "./QueuedChatIndicator";
import { SelectedChatIndicator } from "./SelectedChatIndicator";
import type { ChatVariantProps } from "./variants";

export interface QueuedChatProps extends ChatVariantProps {
    chat: ChatTask;
    onChatSelected: (chat: ChatTask) => void;
    isChatSelected: boolean;
}

export const QueuedChat = forwardRef<HTMLDivElement, QueuedChatProps>(
    ({ onChatSelected, variant, variantColor, chat, isChatSelected, ...rest }, ref) => {
        const messages = useActiveChatMessages(chat);
        const lastMessageDate = messages.length > 0 ? messages[messages.length - 1]?.dateCreated : null;
        const { changeStateAt, stateLastChangedAt, unreadMessageCount } = chat;

        /** Memoized session ID that only changes when the task ID changes */
        const sessionId = useMemo(() => {
            const session = getSessionByTaskId(chat.id);
            if (!session) {
                throw new Error(`Session not found for task ID: ${chat.id} in chat queue`); // should be an impossible case
            }
            return session.id;
        }, [chat.id]);

        const getRingTimerProps = useCallback(() => {
            if (changeStateAt && stateLastChangedAt) {
                const totalTimerDuration = changeStateAt.getTime() - stateLastChangedAt.getTime();
                return {
                    initialPercentage: Math.floor(
                        (100 * (Date.now() - stateLastChangedAt.getTime())) / totalTimerDuration,
                    ),
                    duration: changeStateAt.getTime() - Date.now(),
                };
            }
            return { initialPercentage: undefined, duration: undefined };
        }, [changeStateAt, stateLastChangedAt]);

        return (
            <Flex
                ref={ref}
                id={`chat-select-chatId:${chat.chatId}`}
                {...rest}
                align={"center"}
                className={classes.queued_chat_interactive_area}
                onClick={() => onChatSelected(chat)}
                data-selected={isChatSelected}
            >
                <SelectedChatIndicator isChatSelected={isChatSelected} />
                <NotificationTimerRing
                    variantColor={variantColor}
                    withRippleAnimation={variant === "neglected"} //TODO: support pre-neglect-warning phase in [EWPM-175]
                    ring={{ size: 73, thickness: 3.5, scaleRatio: 1 }}
                    timer={getRingTimerProps()}
                >
                    <Center>
                        <QueuedChatIndicator
                            color="asurion.5"
                            size={20}
                            label={unreadMessageCount}
                            visible={variant !== "incoming" && !!unreadMessageCount}
                        >
                            <ChatAvatar
                                sessionId={sessionId}
                                customerName={chat.customerName}
                                variant={variant}
                                variantColor={variantColor}
                                avatarSize={48}
                            />
                        </QueuedChatIndicator>
                    </Center>
                </NotificationTimerRing>
                <ChatPreviewBuilder
                    chat={chat}
                    lastMessageDate={lastMessageDate}
                    variant={variant}
                    variantColor={variantColor}
                    wrapper={Stack}
                />
            </Flex>
        );
    },
);

QueuedChat.displayName = "QueuedChat";
